import React, { useState, useEffect, useRef } from 'react';
import esignatureApi from '../services/esignature';
import './ESignature.css';

const STATUT_LABELS = {
  en_attente: { label: 'En attente', color: 'var(--color-warning)', bg: 'light-dark(#fef3c7, var(--color-warning-light))' },
  signe:      { label: 'Signé',      color: 'var(--color-success)', bg: 'light-dark(#d1fae5, var(--color-success-light))' },
  refuse:     { label: 'Refusé',     color: 'light-dark(#ef4444, var(--color-error-dark))', bg: 'light-dark(#fee2e2, var(--color-error-light))' },
  expire:     { label: 'Expiré',     color: 'light-dark(#6b7280, var(--text-secondary))', bg: 'light-dark(#f3f4f6, var(--bg-muted))' },
};

function ESignature({ user, editMode, setHeaderAction }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [copied, setCopied] = useState(null);
  const fileRef = useRef(null);

  // Formulaire de création
  const [form, setForm] = useState({
    titre: '', client_nom: '', client_email: '', client_telephone: '', expires_days: '7',
  });
  const [formFile, setFormFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createdUrl, setCreatedUrl] = useState(null);

  // Boutons d'action dans le header
  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({
      primary: { label: '+ Nouvelle signature', onClick: () => setShowModal(true) },
    });
  }, [setHeaderAction]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await esignatureApi.getAll({ statut: filterStatut || undefined, search: search || undefined });
      setItems(res.data.esignatures);
    } catch (error) {
      console.error('Erreur chargement e-signatures:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [filterStatut]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadData();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formFile) return alert('Veuillez sélectionner un document PDF');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('document', formFile);
      fd.append('titre', form.titre);
      fd.append('client_nom', form.client_nom);
      fd.append('client_email', form.client_email);
      fd.append('client_telephone', form.client_telephone);
      fd.append('expires_days', form.expires_days);

      const res = await esignatureApi.create(fd);
      setCreatedUrl(res.data.signature_url);
      loadData();
    } catch (error) {
      console.error('Erreur création:', error);
      alert('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande de signature ?')) return;
    try {
      await esignatureApi.delete(id);
      loadData();
      if (showDetail?.id === id) setShowDetail(null);
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const handleRenvoyer = async (id) => {
    try {
      const res = await esignatureApi.renvoyer(id, { expires_days: 7 });
      copyToClipboard(res.data.signature_url, id);
      loadData();
    } catch (error) {
      console.error('Erreur renvoi:', error);
    }
  };

  const handleViewDetail = async (id) => {
    try {
      const res = await esignatureApi.getById(id);
      setShowDetail(res.data);
    } catch (error) {
      console.error('Erreur détail:', error);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const resetModal = () => {
    setShowModal(false);
    setForm({ titre: '', client_nom: '', client_email: '', client_telephone: '', expires_days: '7' });
    setFormFile(null);
    setCreatedUrl(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';

  if (loading) return <div className="esig-loading">Chargement...</div>;

  return (
    <div className="page-container">
      {/* Onglet unique */}
      <div className="page-tabs">
        <button className="active">✍️ E-Signature</button>
      </div>

      {/* ── Barre d'actions ── */}
      <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => { setSearch(e.target.value); }}
          onKeyDown={e => { if (e.key === 'Enter') handleSearch(e); }}
        />
        <select
          value={filterStatut}
          onChange={e => setFilterStatut(e.target.value)}
          className={`page-select${filterStatut ? ' active-filter' : ''}`}
        >
          <option value="">Tous les statuts</option>
          <option value="en_attente">En attente</option>
          <option value="signe">Signé</option>
          <option value="refuse">Refusé</option>
          <option value="expire">Expiré</option>
        </select>
      </div>

      {/* ── Stats rapides ── */}
      <div className="esig-stats">
        <div className="esig-stat-card">
          <span className="esig-stat-number">{items.length}</span>
          <span className="esig-stat-label">Total</span>
        </div>
        <div className="esig-stat-card">
          <span className="esig-stat-number" style={{ color: 'light-dark(var(--color-warning), var(--color-warning-dark))' }}>
            {items.filter(i => i.statut === 'en_attente').length}
          </span>
          <span className="esig-stat-label">En attente</span>
        </div>
        <div className="esig-stat-card">
          <span className="esig-stat-number" style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))' }}>
            {items.filter(i => i.statut === 'signe').length}
          </span>
          <span className="esig-stat-label">Signés</span>
        </div>
      </div>

      {/* ── Tableau ── */}
      <div className="table-container">
        <table className="esig-table">
          <thead>
            <tr>
              <th>Titre</th>
              <th>Client</th>
              <th>Statut</th>
              <th>Créé le</th>
              <th>Expire le</th>
              <th>Signé le</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="7" className="esig-empty">Aucune demande de signature</td></tr>
            )}
            {items.map(item => {
              const s = STATUT_LABELS[item.statut] || STATUT_LABELS.en_attente;
              return (
                <tr key={item.id}>
                  <td className="esig-td-titre" onClick={() => handleViewDetail(item.id)}>
                    {item.titre}
                    <span className="esig-doc-name">{item.document_name}</span>
                  </td>
                  <td>
                    <div className="esig-client-info">
                      <span className="esig-client-name">{item.client_nom}</span>
                      {item.client_email && <span className="esig-client-email">{item.client_email}</span>}
                    </div>
                  </td>
                  <td>
                    <span className="esig-badge" style={{ color: s.color, background: s.bg }}>
                      {s.label}
                    </span>
                  </td>
                  <td className="esig-td-date">{formatDate(item.created_at)}</td>
                  <td className="esig-td-date">{formatDate(item.expires_at)}</td>
                  <td className="esig-td-date">{formatDate(item.signed_at)}</td>
                  <td className="esig-td-actions">
                    <button className="btn-icon" onClick={() => handleViewDetail(item.id)} title="Voir le détail">
                      👁️
                    </button>
                    {item.statut === 'en_attente' && (
                      <button
                        className="btn-icon"
                        onClick={() => {
                          const baseUrl = window.location.origin;
                          copyToClipboard(`${baseUrl}/signer/${item.token}`, item.id);
                        }}
                        title="Copier le lien"
                      >
                        {copied === item.id ? '✅' : '🔗'}
                      </button>
                    )}
                    {(item.statut === 'expire' || item.statut === 'refuse') && (
                      <button className="btn-icon" onClick={() => handleRenvoyer(item.id)} title="Regénérer le lien">
                        🔄
                      </button>
                    )}
                    {editMode && (
                      <button className="btn-icon delete" onClick={() => handleDelete(item.id)} title="Supprimer">
                        🗑️
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Modal création ── */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{createdUrl ? '✅ Lien de signature créé' : '✍️ Nouvelle demande de signature'}</h3>
              <button className="modal-close" onClick={resetModal}>✕</button>
            </div>

            {createdUrl ? (
              <div className="modal-body">
                <p className="esig-success-msg">
                  Le lien de signature a été généré. Copiez-le et envoyez-le à votre client par le moyen de votre choix (SMS, WhatsApp, email...).
                </p>
                <div className="esig-link-box">
                  <input type="text" readOnly value={createdUrl} className="esig-link-input" />
                  <button
                    className="esig-btn-primary"
                    onClick={() => copyToClipboard(createdUrl, 'modal')}
                  >
                    {copied === 'modal' ? '✅ Copié !' : '📋 Copier'}
                  </button>
                </div>
                <button className="esig-btn-secondary" onClick={resetModal} style={{ marginTop: '16px', width: '100%' }}>
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreate} className="modal-body">
                <div className="esig-form-group">
                  <label>Titre du document *</label>
                  <input
                    type="text"
                    required
                    value={form.titre}
                    onChange={e => setForm({ ...form, titre: e.target.value })}
                    placeholder="Ex : Devis réparation Clio"
                  />
                </div>
                <div className="esig-form-group">
                  <label>Document PDF *</label>
                  <input
                    type="file"
                    accept=".pdf"
                    required
                    ref={fileRef}
                    onChange={e => setFormFile(e.target.files[0])}
                  />
                </div>
                <div className="esig-form-row">
                  <div className="esig-form-group">
                    <label>Nom du client *</label>
                    <input
                      type="text"
                      required
                      value={form.client_nom}
                      onChange={e => setForm({ ...form, client_nom: e.target.value })}
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div className="esig-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={form.client_email}
                      onChange={e => setForm({ ...form, client_email: e.target.value })}
                      placeholder="jean@email.com"
                    />
                  </div>
                </div>
                <div className="esig-form-row">
                  <div className="esig-form-group">
                    <label>Téléphone</label>
                    <input
                      type="tel"
                      value={form.client_telephone}
                      onChange={e => setForm({ ...form, client_telephone: e.target.value })}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div className="esig-form-group">
                    <label>Expiration (jours)</label>
                    <select
                      value={form.expires_days}
                      onChange={e => setForm({ ...form, expires_days: e.target.value })}
                    >
                      <option value="3">3 jours</option>
                      <option value="7">7 jours</option>
                      <option value="14">14 jours</option>
                      <option value="30">30 jours</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="esig-btn-secondary" onClick={resetModal}>Annuler</button>
                  <button type="submit" className="esig-btn-primary" disabled={saving}>
                    {saving ? 'Envoi en cours...' : '✍️ Créer le lien de signature'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Modal détail ── */}
      {showDetail && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📄 {showDetail.esignature.titre}</h3>
              <button className="modal-close" onClick={() => setShowDetail(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="esig-detail-grid">
                <div className="esig-detail-item">
                  <span className="esig-detail-label">Client</span>
                  <span>{showDetail.esignature.client_nom}</span>
                </div>
                <div className="esig-detail-item">
                  <span className="esig-detail-label">Email</span>
                  <span>{showDetail.esignature.client_email || '—'}</span>
                </div>
                <div className="esig-detail-item">
                  <span className="esig-detail-label">Téléphone</span>
                  <span>{showDetail.esignature.client_telephone || '—'}</span>
                </div>
                <div className="esig-detail-item">
                  <span className="esig-detail-label">Statut</span>
                  <span className="esig-badge" style={{
                    color: (STATUT_LABELS[showDetail.esignature.statut] || {}).color,
                    background: (STATUT_LABELS[showDetail.esignature.statut] || {}).bg,
                  }}>
                    {(STATUT_LABELS[showDetail.esignature.statut] || {}).label}
                  </span>
                </div>
                <div className="esig-detail-item">
                  <span className="esig-detail-label">Créé le</span>
                  <span>{formatDate(showDetail.esignature.created_at)}</span>
                </div>
                <div className="esig-detail-item">
                  <span className="esig-detail-label">Expire le</span>
                  <span>{formatDate(showDetail.esignature.expires_at)}</span>
                </div>
                {showDetail.esignature.signed_at && (
                  <>
                    <div className="esig-detail-item">
                      <span className="esig-detail-label">Signé le</span>
                      <span>{formatDate(showDetail.esignature.signed_at)}</span>
                    </div>
                    <div className="esig-detail-item">
                      <span className="esig-detail-label">IP signature</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{showDetail.esignature.signature_ip}</span>
                    </div>
                  </>
                )}
                {showDetail.esignature.document_hash && (
                  <div className="esig-detail-item esig-detail-full">
                    <span className="esig-detail-label">Hash document original (SHA-256)</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                      {showDetail.esignature.document_hash}
                    </span>
                  </div>
                )}
              </div>

              <div className="esig-detail-actions">
                {showDetail.document_url && (
                  <a href={showDetail.document_url} target="_blank" rel="noopener noreferrer" className="esig-btn-secondary">
                    📄 Voir le document original
                  </a>
                )}
                {showDetail.signed_document_url && (
                  <a href={showDetail.signed_document_url} target="_blank" rel="noopener noreferrer" className="esig-btn-primary">
                    ✅ Télécharger le document signé
                  </a>
                )}
                {showDetail.esignature.statut === 'en_attente' && (
                  <button
                    className="esig-btn-secondary"
                    onClick={() => copyToClipboard(showDetail.signature_url, 'detail')}
                  >
                    {copied === 'detail' ? '✅ Copié !' : '🔗 Copier le lien de signature'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ESignature;
